import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, Phone } from "@phosphor-icons/react";
import { BRAND, NAV_LINKS } from "../lib/data";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#FAF9F6]/90 backdrop-blur-xl border-b border-brand-border"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-20">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                    <img
                        src={BRAND.logoLight}
                        alt={BRAND.name}
                        className="h-12 w-12 object-contain rounded-md"
                    />
                    <div className="hidden sm:flex flex-col leading-tight">
                        <span className="font-serif text-xl text-brand-primary font-semibold">{BRAND.short}</span>
                        <span className="text-[11px] uppercase tracking-[0.2em] text-brand-secondary">Est. {BRAND.established}</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm font-medium tracking-wide transition-colors ${
                                    isActive ? "text-brand-primary" : "text-brand-textMuted hover:text-brand-primary"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={`tel:${BRAND.officePhone.replace(/\D/g, "")}`}
                        data-testid="header-call-link"
                        className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-brand-primary hover:text-brand-primaryHover transition-colors"
                    >
                        <Phone size={18} weight="duotone" />
                        {BRAND.officePhone}
                    </a>
                    <Link
                        to="/contact"
                        data-testid="header-quote-cta"
                        className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primaryHover transition-all hover:-translate-y-0.5"
                    >
                        Get a Free Quote
                    </Link>
                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden p-2 text-brand-primary"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={26} /> : <List size={26} />}
                    </button>
                </div>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="lg:hidden bg-[#FAF9F6] border-t border-brand-border">
                    <div className="px-4 py-6 flex flex-col gap-4">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-base font-medium ${isActive ? "text-brand-primary" : "text-brand-textMuted"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="mobile-quote-cta"
                            className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-brand-primary text-white text-sm font-semibold"
                        >
                            Get a Free Quote
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
